
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';

export const SubscriptionTestPanel = () => {
  const { user } = useAuth();
  const { subscribed, subscriptionTier, loading, checkSubscription } = useSubscription();
  const { toast } = useToast();
  const [testResults, setTestResults] = useState<Record<string, 'pass' | 'fail' | 'pending' | 'info'>>({});
  const [isRunning, setIsRunning] = useState(false);

  const addTestResult = (testName: string, status: 'pass' | 'fail' | 'pending' | 'info', message?: string) => {
    setTestResults(prev => ({ ...prev, [testName]: status }));
    if (message) {
      toast({
        title: `Test: ${testName}`,
        description: message,
        variant: status === 'fail' ? 'destructive' : 'default'
      });
    }
  };

  const runComprehensiveTests = async () => {
    setIsRunning(true);
    setTestResults({});

    // Test 1: Authentication Check
    addTestResult('Authentication', user ? 'pass' : 'fail', 
      user ? `User authenticated: ${user.email}` : 'User not authenticated - login required');

    if (!user) {
      setIsRunning(false);
      return;
    }

    // Test 2: Subscription Status Check
    addTestResult('Subscription Status', 'pending');
    try {
      await checkSubscription();
      addTestResult('Subscription Status', 'pass', 
        `Current status: ${subscribed ? `Active (${subscriptionTier})` : 'Not subscribed'}`);
    } catch (error) {
      addTestResult('Subscription Status', 'fail', `Failed to check subscription: ${error instanceof Error ? error.message : String(error)}`);
    }

    // Test 3: Edge Function Connectivity
    addTestResult('Edge Functions', 'pending');
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (error) throw error;
      addTestResult('Edge Functions', 'pass', 'check-subscription function accessible');
    } catch (error) {
      addTestResult('Edge Functions', 'fail', `Edge function error: ${error instanceof Error ? error.message : String(error)}`);
    }

    // Test 4: Stripe Integration Test (create a test session without payment)
    addTestResult('Stripe Integration', 'pending');
    try {
      // This will test the create-checkout function without actually proceeding to payment
      const response = await fetch('/api/test-stripe-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        addTestResult('Stripe Integration', 'pass', 'Stripe connection verified');
      } else {
        addTestResult('Stripe Integration', 'info', 'Direct Stripe test not available - use manual payment test');
      }
    } catch (error) {
      addTestResult('Stripe Integration', 'info', 'Use create-checkout function for Stripe testing');
    }

    // Test 5: Environment Check
    const isDev = window.location.hostname === 'localhost';
    addTestResult('Environment', 'info', 
      `Running in ${isDev ? 'Development' : 'Production'} mode`);

    setIsRunning(false);
  };

  const testCreateCheckout = async (plan: string) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to test checkout",
        variant: "destructive"
      });
      return;
    }

    try {
      addTestResult(`Checkout Test (${plan})`, 'pending');
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan: plan.toLowerCase() }
      });

      if (error) throw error;

      if (data?.url) {
        addTestResult(`Checkout Test (${plan})`, 'pass', 'Checkout session created successfully');
        toast({
          title: "Checkout Session Created",
          description: "Ready to test payment flow - session URL generated",
        });
        // Don't automatically open in test mode
        console.log('Checkout URL:', data.url);
      } else {
        addTestResult(`Checkout Test (${plan})`, 'fail', 'No checkout URL returned');
      }
    } catch (error) {
      addTestResult(`Checkout Test (${plan})`, 'fail', `Checkout creation failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const clearResults = () => {
    setTestResults({});
  };

  const getStatusIcon = (status: 'pass' | 'fail' | 'pending' | 'info') => {
    switch (status) {
      case 'pass': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'fail': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending': return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'info': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: 'pass' | 'fail' | 'pending' | 'info') => {
    switch (status) {
      case 'pass': return 'bg-green-100 text-green-800';
      case 'fail': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-blue-100 text-blue-800';
      case 'info': return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <Card className="w-full max-w-4xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Subscription Flow Test Panel
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Status */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border rounded-lg">
            <h3 className="font-semibold mb-2">Authentication</h3>
            <Badge variant={user ? "default" : "destructive"}>
              {user ? "Authenticated" : "Not Authenticated"}
            </Badge>
            {user && <p className="text-sm text-muted-foreground mt-1">{user.email}</p>}
          </div>
          
          <div className="p-4 border rounded-lg">
            <h3 className="font-semibold mb-2">Subscription</h3>
            <Badge variant={subscribed ? "default" : "secondary"}>
              {subscribed ? `Active (${subscriptionTier})` : "Not Subscribed"}
            </Badge>
          </div>
          
          <div className="p-4 border rounded-lg">
            <h3 className="font-semibold mb-2">Environment</h3>
            <Badge variant="outline">
              {window.location.hostname === 'localhost' ? 'Development' : 'Production'}
            </Badge>
          </div>
        </div>

        {/* Test Controls */}
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={runComprehensiveTests} 
            disabled={isRunning}
            className="flex items-center gap-2"
          >
            {isRunning && <Loader2 className="h-4 w-4 animate-spin" />}
            Run All Tests
          </Button>
          
          <Button 
            onClick={() => testCreateCheckout('starter')} 
            variant="outline"
            disabled={!user}
          >
            Test Starter Checkout
          </Button>
          
          <Button 
            onClick={() => testCreateCheckout('premium')} 
            variant="outline"
            disabled={!user}
          >
            Test Premium Checkout
          </Button>
          
          <Button 
            onClick={clearResults} 
            variant="ghost"
          >
            Clear Results
          </Button>
        </div>

        {/* Test Results */}
        {Object.keys(testResults).length > 0 && (
          <div className="space-y-2">
            <h3 className="font-semibold">Test Results</h3>
            <div className="space-y-1">
              {Object.entries(testResults).map(([testName, status]) => (
                <div key={testName} className="flex items-center gap-2 p-2 rounded border">
                  {getStatusIcon(status)}
                  <span className="flex-1">{testName}</span>
                  <Badge className={getStatusColor(status)}>
                    {status.toUpperCase()}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Manual Test Instructions */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h3 className="font-semibold mb-2">Manual Payment Flow Test Steps</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Ensure you're authenticated (sign in if needed)</li>
            <li>Run "Run All Tests" to verify system readiness</li>
            <li>Use "Test [Plan] Checkout" to create a test session</li>
            <li>Check browser console for checkout URL</li>
            <li>Use Stripe test cards for payment testing</li>
            <li>Verify redirect back to success page</li>
            <li>Check subscription status updates correctly</li>
            <li>Test cancellation flow from customer portal</li>
          </ol>
          
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm font-medium text-yellow-800">⚠️ Stripe Test Cards:</p>
            <p className="text-xs text-yellow-700 mt-1">
              Success: 4242 4242 4242 4242 | Decline: 4000 0000 0000 0002
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
